import { DataCenterFinancialSnapshot } from '../../financial/DataCenterFinancialSnapshot';
/**
 * SensitivitySection (FIN-2) — what moves this project's economics.
 *
 * READ-ONLY over the persisted stamp, and specifically over the FA-BASIS
 * material: `financialAnalysis.sensitivity` (a tornado over THIS model's
 * drivers, each perturbed by a fixed delta and recomputed through the engine)
 * and `financialAnalysis.scenarioMetrics` (the project's own named scenarios).
 *
 * HISTORY — this section used to render the BENCHMARK-PATHWAY Monte-Carlo
 * percentiles under the headings "Downside / Base / Upside". Those percentiles
 * describe what a grid / gas / SMR option of this size would cost against a flat
 * value-of-served-load credit; they are a technology comparison, not a range
 * around this project's NPV. Presenting them as the project's scenarios put a
 * minus-$694M "downside" beside a plus-$611M headline. The pathway band still
 * has a home — `MonteCarloBandsSection`, explicitly labelled — and this section
 * now shows the project's own numbers.
 *
 * NEVER re-derives the sensitivity math: it renders a snapshot taken at
 * generation time, so it cannot drift from the verdict printed beside it.
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { useFigureNumber } from '../../report/figureCounter';
import {
  EditorialFigureCaption,
  EditorialKpiRail,
} from '../../report/editorial';
import { FIN_MONO, fmtUsdMillions } from '../../../../lib/reportRendering/financialFormat';

function isNum(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export const SensitivitySection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const figNo = useFigureNumber('financial:sensitivityTornado');
  const fa = report.financialAnalysis;
  const sensitivity = fa?.sensitivity;
  const scenarios = fa?.scenarioMetrics ?? [];
  const drivers = fa?.bindingConstraints ?? [];

  // Only rows the engine actually produced a swing for. A row with a null
  // `swingAbs` used to draw the 4% minimum-width bar and take the "improves
  // with increase" tint from `(null ?? 0) >= (null ?? 0)`, i.e. a green bar
  // asserting a direction nothing computed.
  const rows = (sensitivity?.rows ?? []).filter(
    (r) => isNum(r.swingAbs) && isNum(r.metricLow) && isNum(r.metricHigh),
  );
  const hasTornado = rows.length > 0 && isNum(sensitivity?.metricBase);
  const hasScenarios = scenarios.length > 0;
  const hasData = hasTornado || hasScenarios || drivers.length > 0;

  // Bar widths are proportional to the LARGEST swing, so the ordering the eye
  // reads is the ordering the engine computed.
  const maxSwing = rows.reduce((m, r) => Math.max(m, r.swingAbs as number), 0);
  const negInk = roleColor(theme, 'negative');
  const posInk = roleColor(theme, 'positive');

  return (
    <SectionShell label={label} hasData={hasData}>
      {hasTornado ? (
        <div data-testid="financial-sensitivity-tornado">
          <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'muted') }}>
            NPV sensitivity &plusmn;{sensitivity!.deltaPct}% per driver
          </div>
          <p className="text-[10px] mb-2" style={{ color: roleColor(theme, 'muted') }}>
            Each driver moved &plusmn;{sensitivity!.deltaPct}% around this project&rsquo;s own model, recomputed through the
            engine. Base NPV {fmtUsdMillions(sensitivity!.metricBase)}. Ordered by how much each one moves the answer.
          </p>
          <ul className="space-y-1.5">
            {rows.map((r) => {
              const width = maxSwing > 0 ? Math.max(4, ((r.swingAbs as number) / maxSwing) * 100) : 0;
              // A driver whose HIGH case is worse than its LOW case (WACC, cost
              // drivers) is inverted — colour by direction, not by position.
              const improvesWithIncrease = (r.metricHigh as number) >= (r.metricLow as number);
              if (report.financialAnalysis?.modelKind === 'data-center-capacity-fee') return <SectionShell label={label} hasData><DataCenterFinancialSnapshot stamp={report.financialAnalysis} section="sensitivity" /></SectionShell>;

  return (
                <li key={r.field} className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-2">
                  <span className="text-[11px] truncate" style={{ color: roleColor(theme, 'body') }}>
                    {r.label}
                    {/* A physical limit held this driver back from the full
                        ±delta (a 90% capacity factor cannot reach 108%), so
                        this bar is not a like-for-like comparison with the
                        others — and because the tornado SORTS BY SWING, a
                        clamped driver under-ranks. Say so on the bar. */}
                    {r.clampedBound ? (
                      <span
                        className="ml-1 text-[9px]"
                        style={{ color: roleColor(theme, 'muted') }}
                        data-testid={`financial-sensitivity-clamped-${r.field}`}
                        title="A physical limit capped this driver below the stated delta, so this bar is smaller than a like-for-like comparison would be."
                      >
                        (capped)
                      </span>
                    ) : null}
                  </span>
                  <span className="h-[10px] rounded-sm" style={{
                    width: `${width}%`,
                    background: improvesWithIncrease ? posInk : negInk,
                    opacity: 0.55,
                  }} />
                  <span className="text-[10px] tabular-nums" style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'muted') }}>
                    {fmtUsdMillions(r.metricLow)} &rarr; {fmtUsdMillions(r.metricHigh)}
                  </span>
                </li>
              );
            })}
          </ul>
          <EditorialFigureCaption theme={theme} number={figNo}>
            Per-driver NPV swing at &plusmn;{sensitivity!.deltaPct}% of each input, recomputed through the
            project-finance engine. Bar width is proportional to the total swing; the endpoints are the low and
            high cases.
            {rows.some((r) => r.clampedBound) ? (
              <> Drivers marked <em>(capped)</em> hit a physical limit before the full &plusmn;{sensitivity!.deltaPct}%
              &ndash; a capacity factor cannot exceed 100% &ndash; so their bars understate how much they matter
              relative to the rest.</>
            ) : null}
          </EditorialFigureCaption>
        </div>
      ) : null}

      {hasScenarios ? (
        <div className="mt-3" data-testid="financial-scenario-strip">
          <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'muted') }}>
            Declared scenarios
          </div>
          <EditorialKpiRail
            theme={theme}
            items={scenarios.map((sc) => ({
              key: sc.isCanonical ? `${sc.label} (basis)` : sc.label,
              value: fmtUsdMillions(sc.npvUsd),
              unit: sc.summary,
              tone: sc.isCanonical ? 'accent' : undefined,
            }))}
          />
        </div>
      ) : null}

      {drivers.length > 0 ? (
        <div className="mt-2">
          <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'muted') }}>
            Binding levers
          </div>
          <ul className="space-y-1" data-testid="financial-sensitive-drivers">
            {drivers.map((d, i) => (
              <li key={`drv-${i}`} className="text-sm flex gap-2" style={{ color: roleColor(theme, 'body') }}>
                <ChevronRight
                  aria-hidden="true"
                  data-pdf-sensitive-driver-mark="true"
                  className="mt-[0.2em] h-[0.85em] w-[0.85em] shrink-0"
                  style={{ color: roleColor(theme, 'accent') }}
                  strokeWidth={1.75}
                />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {!hasScenarios ? (
        <p className="text-[10px]" style={{ color: roleColor(theme, 'muted') }} data-testid="financial-no-scenarios-note">
          This model defines no named scenarios, so no upside / downside case is reported. The benchmark-pathway
          range shown elsewhere in this report describes technology options, not this project.
        </p>
      ) : null}
    </SectionShell>
  );
};
